import { FilterBase } from './filter-base.js';

/**
 * Language filter component.
 * Provides an input with autocomplete for language selection.
 * @class
 * @extends {FilterBase}
 */
class FilterLanguage extends FilterBase {
    /**
     * Initialize the language filter component.
     * @constructor
     */
    constructor() {
        super();
        /** @type {HTMLInputElement|null} The text input element */
        this.inputElement = null;
        /** @type {HTMLDivElement|null} Container for language suggestions */
        this.suggestionsContainer = null;
        /** @type {Object} Map of language codes to names */
        this.choices = {};
        /** @type {number} Index of highlighted suggestion (-1 means none) */
        this.highlightIndex = -1;
    }

    /**
     * Renders the language filter component.
     * Creates an input field and suggestions container.
     */
    render() {
        super.render();
        if (!this.filterData?.conf?.choices) {
            console.warn('LanguageFilter: Missing configuration data (conf.choices).', this.filterData);
            const errorMsg = document.createElement('span');
            errorMsg.textContent = ' [Configuration Error]';
            errorMsg.style.color = 'red';
            this.appendChild(errorMsg);
            return;
        }

        this.choices = this.filterData.conf.choices;
        this.inputElement = document.createElement('input');
        this.inputElement.type = 'text';
        this.inputElement.id = `filter-input-${this.filterData.name}`;
        this.inputElement.name = this.filterData.name;
        this.inputElement.placeholder = `Enter ${this.filterData.label}...`;
        this.appendChild(this.inputElement);

        this.suggestionsContainer = document.createElement('div');
        this.suggestionsContainer.className = 'language-suggestions';
        this.suggestionsContainer.style.display = 'none';
        this.appendChild(this.suggestionsContainer);

        this._bindEvents();
    }

    /**
     * Binds event listeners to the input and document.
     * @private
     */
    _bindEvents() {
        this.inputElement.addEventListener('input', () => this._onInput());
        this.inputElement.addEventListener('keydown', (e) => this._onKeyDown(e));
        document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) this._hideSuggestions();
        });
    }

    /**
     * Handles input events on the language input.
     * Filters and displays matching language suggestions.
     * @private
     */
    _onInput() {
        const parts = this.inputElement.value.split(',');
        const last = parts.pop().trim().toLowerCase();
        if (!last) return this._hideSuggestions();
        const matches = Object.entries(this.choices)
            .filter(([code, name]) =>
                code.startsWith(last) || name.toLowerCase().startsWith(last)
            )
            .slice(0, 10);
        this._showSuggestions(matches);
    }

    /**
     * Shows the suggestions dropdown with matching languages.
     * @param {Array<Array<string>>} list - Array of [code, name] pairs to display
     * @private
     */
    _showSuggestions(list) {
        this.suggestionsContainer.innerHTML = '';
        this.highlightIndex = -1;
        list.forEach(([code, name], idx) => {
            const div = document.createElement('div');
            div.textContent = `${name} (${code})`;
            div.dataset.code = code;
            div.addEventListener('click', (e) => {
                e.preventDefault();
                this._selectCode(code);
            });
            this.suggestionsContainer.appendChild(div);
        });

        // If we have suggestions, highlight the first one by default
        if (list.length > 0) {
            this.highlightIndex = 0;
            this._updateHighlight(Array.from(this.suggestionsContainer.children));
        }

        this.suggestionsContainer.style.display = list.length ? 'block' : 'none';
    }

    /**
     * Hides the suggestions dropdown.
     * @private
     */
    _hideSuggestions() {
        this.suggestionsContainer.style.display = 'none';
        this.highlightIndex = -1;
    }

    /**
     * Handles keyboard navigation in the suggestions dropdown.
     * @param {KeyboardEvent} e - The keyboard event
     * @private
     */
    _onKeyDown(e) {
        const items = Array.from(this.suggestionsContainer.children);
        if (this.suggestionsContainer.style.display === 'none' || !items.length) return;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.highlightIndex = (this.highlightIndex + 1) % items.length;
                this._updateHighlight(items);
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.highlightIndex = (this.highlightIndex - 1 + items.length) % items.length;
                this._updateHighlight(items);
                break;
            case 'Enter':
                if (this.highlightIndex >= 0) {
                    e.preventDefault();
                    const selectedItem = items[this.highlightIndex];
                    if (selectedItem && selectedItem.dataset.code) {
                        this._selectCode(selectedItem.dataset.code);
                    }
                }
                break;
            case 'Escape':
                this._hideSuggestions();
                break;
            case 'Tab':
                if (this.highlightIndex >= 0) {
                    e.preventDefault();
                    const selectedItem = items[this.highlightIndex];
                    if (selectedItem && selectedItem.dataset.code) {
                        this._selectCode(selectedItem.dataset.code);
                    }
                }
                break;
        }
    }

    /**
     * Updates the highlighted item in the suggestions dropdown.
     * @param {Array<HTMLElement>} items - The suggestion items
     * @private
     */
    _updateHighlight(items) {
        items.forEach((item, i) => {
            item.classList.toggle('highlighted', i === this.highlightIndex);
            if (i === this.highlightIndex) {
                item.scrollIntoView({ block: 'nearest' });
                // Add visual styling for the highlighted item
                item.classList.add('highlighted');
            } else {
                item.classList.remove('highlighted');
            }
        });
    }

    /**
     * Selects a language code and updates the input value.
     * @param {string} code - The language code to select
     * @private
     */
    _selectCode(code) {
        const parts = this.inputElement.value.split(',');
        parts.pop();
        parts.push(code);
        this.inputElement.value = parts.map(p => p.trim()).join(', ');
        this._hideSuggestions();
        this.inputElement.focus();
        this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    }

    /**
     * Gets the current value as an array of language codes.
     * @returns {Array<string>} Array of selected language codes
     */
    getValue() {
        return this.inputElement
            ? this.inputElement.value.split(',').map(s => s.trim()).filter(Boolean)
            : [];
    }
}

customElements.define('filter-language', FilterLanguage);
